import { Button, Form, Input, notification, Row } from 'antd';
import { ConfirmationToken } from 'components/ConfirmationToken';
import { Modal } from 'components/Modal';
import { TextL, TextM, TextS } from 'components/Text';
import { normalizeCpf } from 'helpers/normalizers';
import { cpfRule, emailRule } from 'helpers/rules';
import React, { useEffect } from 'react';
import theme from 'theme/theme';
import { User } from 'types/User';

export interface IConfirmationResetUserAccessModalProps {
	isOpen: boolean;
	title: string;
	isLoading: boolean;
	userData?: User;
	onClose: () => void;
	onConfirm: (user: User, mfaToken: string) => void;
}

export interface UserAccess {
	email_address: string;
	taxpayer_id: string;
	full_name: string;
}

const ConfirmationResetUserAccessModal = ({
	isOpen,
	title,
	isLoading,
	userData,
	onClose,
	onConfirm,
}: IConfirmationResetUserAccessModalProps) => {
	const [form] = Form.useForm();
	const [api, contextHolder] = notification.useNotification();
	const [formValid, setFormValid] = React.useState(true);

	const [currentStep, setCurrentStep] = React.useState(0);

	const handleConfirm = (mfaToken: string) => {
		if (currentStep === 1) {
			if (!userData) return;
			onConfirm(
				{
					...userData,
					email_address: form.getFieldValue('email_address'),
				},
				mfaToken,
			);
		}
	};

	const handleClose = () => {
		onClose();
	};

	useEffect(() => {
		if (isOpen) {
			form.resetFields();
			setCurrentStep(0);
		}
	}, [isOpen]);

	return (
		<Modal title={title} isOpen={isOpen} onClose={handleClose}>
			{contextHolder}
			<main
				style={{
					display: 'flex',
					justifyContent: 'center',
					alignItems: 'center',
					minHeight: '100px',
				}}
			>
				{currentStep === 0 && (
					<div>
						<TextL>
							Confirme o e-mail do usuário para resetar o acesso.
						</TextL>
						<Form<UserAccess>
							style={{ marginTop: '1.5rem', width: '100%' }}
							form={form}
							layout="vertical"
							initialValues={{
								email_address: userData?.email_address || '',
								taxpayer_id: normalizeCpf(
									userData?.taxpayer_id || '',
								),
								full_name: userData?.full_name || '',
							}}
							onFieldsChange={(f, allFields) => {
								const errors = allFields.filter(
									field =>
										field.errors && field.errors.length > 0,
								);
								setFormValid(errors.length === 0);
							}}
						>
							<Form.Item
								name="taxpayer_id"
								label="CPF"
								normalize={e => normalizeCpf(e)}
								rules={[
									{
										required: true,
										message: 'Campo obrigatório',
									},
									cpfRule,
								]}
							>
								<Input
									disabled
									placeholder="Ex.: 000.000.000-00"
								/>
							</Form.Item>
							<Form.Item
								name="full_name"
								label="Nome"
								rules={[
									{
										required: true,
										message: 'Campo obrigatório',
									},
									{
										max: 50,
										message: 'Máximo de 50 caracteres',
									},
								]}
							>
								<Input disabled placeholder="Ex.: João Silva" />
							</Form.Item>
							<Form.Item
								name="email_address"
								label="E-mail"
								rules={[
									{
										required: true,
										message: 'Campo obrigatório',
									},
									emailRule('email_address'),
								]}
							>
								<Input placeholder="Ex.: exemplo@exemplo.com" />
							</Form.Item>
						</Form>
						<TextL style={{ marginTop: '2rem' }}>
							Você tem certeza que deseja resetar o acesso do
							usuário?
						</TextL>
					</div>
				)}
				{currentStep === 1 && (
					<ConfirmationToken
						isLoading={isLoading}
						onClose={onClose}
						onConfirm={token => handleConfirm(token)}
					>
						<TextM>
							Para sua segurança, realize a autenticação do seu
							dispositivo para aprovar o reset de acesso de
							usuário.
						</TextM>
					</ConfirmationToken>
				)}
			</main>

			{
				// Buttons
				currentStep === 0 && (
					<Row
						style={{
							flexDirection: 'column',
							marginTop: '2rem',
							gap: '1rem',
						}}
					>
						<Button
							type="primary"
							disabled={!formValid}
							onClick={() => {
								form.validateFields()
									.then(() => {
										setCurrentStep(1);
									})
									.catch(() => {
										api.error({
											message:
												'Por favor, preencha o campo de e-mail corretamente.',
										});
									});
							}}
							style={{
								height: '45px',
							}}
						>
							<TextM
								style={{
									color: theme.white,
								}}
							>
								{currentStep === 0 ? 'Confirmar' : 'Resetar'}
							</TextM>
						</Button>
						<Button
							type="text"
							onClick={handleClose}
							style={{
								height: '45px',
							}}
						>
							<TextM
								style={{
									color: theme.primary,
								}}
							>
								Cancelar
							</TextM>
						</Button>
					</Row>
				)
			}
		</Modal>
	);
};

export default ConfirmationResetUserAccessModal;
